import React, { useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import WineRating from "../../atoms/WineRating";
import {
  GET_PRODUCT_RATING_DETAILS,
  UPDATE_PRODUCT_RATING,
  UPDATE_PRODUCT_RATING_DETAILS,
} from "../../../graphql/resolvers/product";
import { trackingEvents, useTrackEvent } from "../../../helpers/Analytics";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import { logError } from "../../../helpers/logging";
import { formatRatingScore } from "../../../helpers/ratings";
import {
  GetProductRatingDetails,
  GetProductRatingDetailsVariables,
} from "../../../types/__generated__/GetProductRatingDetails";
import {
  UpdateProductRatingDetails,
  UpdateProductRatingDetailsVariables,
} from "../../../types/__generated__/UpdateProductRatingDetails";

import {
  BooleanChoiceButton,
  DetailControl,
  DetailLabel,
  DetailRow,
  DetailRows,
  DetailsWrapper,
  DoneButton,
  NoteTextarea,
  PanelHeading,
  ReadOnlyValue,
} from "./style";

export const RATING_ATTRIBUTE_KINDS = {
  SCALE: "SCALE",
  BOOLEAN: "BOOLEAN",
} as const;

/* Structural shapes the generated query types (GetMemberCellar,
 * GetProductRatingDetails, ...) all satisfy — nullability mirrors the
 * schema's graphene defaults so callers can pass generated objects through
 * without casting. */
export interface RatingAttribute {
  id?: number | null;
  key: string;
  label: string;
  kind: string | null;
}

export interface ProductRatingDetail {
  attribute: {
    id?: number | null;
    key: string;
  };
  scaleValue: number | null;
  booleanValue: boolean | null;
}

interface DetailValue {
  scaleValue: number | null;
  booleanValue: boolean | null;
}

interface ProductRatingDetailsProps {
  productId: number;
  attributes: RatingAttribute[];
  details?: ProductRatingDetail[] | null;
  /** The rows appear as a direct continuation of rating the product — the
      card opens them the moment an overall rating is committed. There is no
      user-facing toggle. Ignored in readOnly mode. */
  isOpen?: boolean;
  /** Renders a small close button in the top-right corner. */
  onClose?: () => void;
  /** The member's overall score for the product; required to save the note
      (the overall-rating mutation always carries both). */
  overallScore?: number | null;
  /** The overall rating's note; edited under the detail rows and saved
      debounced / on close. */
  note?: string | null;
  /** Render the tasting-note textarea. Off when the host surface already has
      its own note editor (the review tray's form). */
  withNote?: boolean;
  /** Overrides the note textarea's placeholder — rewards aren't tasted, so
      their card panel passes a neutral prompt instead of the tasting copy. */
  notePlaceholder?: string;
  /** Read-back mode: rows with a value render as static text/stars, rows
      without one don't render at all. */
  readOnly?: boolean;
  /** "compact" is the card panel (small-caps labels, medium stars);
      "form" matches the review tray's own text and star sizes, with the
      hierarchy carried by spacing instead. */
  variant?: "compact" | "form";
  className?: string;
}

const valuesByAttributeKey = (details?: ProductRatingDetail[] | null): Record<string, DetailValue> =>
  (details ?? []).reduce<Record<string, DetailValue>>(
    (acc, detail) => ({
      ...acc,
      [detail.attribute.key]: { scaleValue: detail.scaleValue, booleanValue: detail.booleanValue },
    }),
    {}
  );

/**
 * The per-attribute detail ratings (e.g. Balance, Drink again) a member can
 * add after their overall product rating. The rows are driven entirely by the
 * product's `ratingAttributes` from the backend, so per-product-type sets
 * need no client changes. Every change saves immediately (one upsert per
 * interaction, mirroring the overall rating's instant save).
 */
const ProductRatingDetails = ({
  productId,
  attributes,
  details = null,
  isOpen = false,
  onClose,
  overallScore = null,
  note = null,
  withNote = true,
  notePlaceholder,
  readOnly = false,
  variant = "compact",
  className,
}: ProductRatingDetailsProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const trackEvent = useTrackEvent();

  /* The values the user just committed, shown until fresh server truth
     arrives through the `details` prop — same pattern as WineRating's
     committedValue, so rows don't flick back mid-save. */
  const [pendingValues, setPendingValues] = useState<Record<string, DetailValue>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  /* Keyed on content, not identity — callers rebuild the details array every
     render, and resetting on identity alone would wipe the user's optimistic
     values whenever anything else in the grid re-rendered. */
  const detailsContentKey = JSON.stringify(details ?? []);
  useEffect(() => {
    setPendingValues({});
  }, [detailsContentKey]);

  const [updateProductRatingDetails] = useMutation<
    UpdateProductRatingDetails,
    UpdateProductRatingDetailsVariables
  >(UPDATE_PRODUCT_RATING_DETAILS);

  const client = useApolloClient();
  const [noteDraft, setNoteDraft] = useState(note ?? "");

  /* The note belongs to the overall rating, so saving it re-sends the
     overall mutation (score + note). It saves 500ms after the last
     keystroke and again from the unmount cleanup (closing the panel) —
     hence the refs (the cleanup must see the latest values) and the raw
     client (the component is gone by then). The textarea is never locked
     during a save: last write wins. */
  const lastSavedNoteRef = useRef(note ?? "");
  const noteStateRef = useRef({ draft: "", score: null as number | null });
  noteStateRef.current = { draft: noteDraft, score: overallScore };

  useEffect(() => {
    /* Fresh server truth for the note only replaces the draft when the user
       hasn't typed past what was last saved — never clobber in-flight
       keystrokes with a stale response. */
    setNoteDraft((current) => (current === lastSavedNoteRef.current ? note ?? "" : current));
    lastSavedNoteRef.current = note ?? "";
  }, [note]);

  const saveNoteIfChanged = () => {
    const { draft, score } = noteStateRef.current;
    if (score == null || score <= 0 || draft === lastSavedNoteRef.current) {
      return;
    }
    lastSavedNoteRef.current = draft;
    client
      .mutate({
        mutation: UPDATE_PRODUCT_RATING,
        variables: { input: { productId, score, note: draft } },
      })
      .catch((error) => {
        logError(error, { location: "ProductRatingDetails", action: "UPDATE_PRODUCT_RATING_NOTE" });
      });
  };

  useEffect(() => {
    const timer = setTimeout(saveNoteIfChanged, 500);
    return () => clearTimeout(timer);
  }, [noteDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => saveNoteIfChanged, []); // eslint-disable-line react-hooks/exhaustive-deps

  const savedValues = valuesByAttributeKey(details);
  const hasAnySavedValue = Object.keys(savedValues).length > 0;

  if (attributes.length === 0) {
    return null;
  }

  const saveDetail = (attributeKey: string, value: DetailValue) => {
    setPendingValues((previous) => ({ ...previous, [attributeKey]: value }));
    setSavingKey(attributeKey);
    updateProductRatingDetails({
      variables: {
        input: {
          productId,
          details: [
            {
              attributeKey,
              scaleValue: value.scaleValue,
              booleanValue: value.booleanValue,
            },
          ],
        },
      },
    })
      .catch((error) => {
        logError(error, { location: "ProductRatingDetails", action: "UPDATE_PRODUCT_RATING_DETAILS" });
      })
      .finally(() => {
        setSavingKey((current) => (current === attributeKey ? null : current));
      });
  };

  const renderScaleRow = (attribute: RatingAttribute, value: DetailValue | undefined) => (
    <WineRating
      rating={value?.scaleValue ?? 0}
      /* The form variant matches the review form's overall star row; the
         compact variant fits the card panel. */
      largeIcon={variant === "form"}
      mediumIcon={variant !== "form"}
      pending={savingKey === attribute.key}
      {...(readOnly
        ? {}
        : {
            onClickRating: (score: number) =>
              saveDetail(attribute.key, { scaleValue: score, booleanValue: null }),
            trackingContext: {
              event: trackingEvents.TRACKING_EVENT_SELECTED_PRODUCT_RATING_DETAIL,
              properties: { attribute_key: attribute.key },
            },
          })}
    />
  );

  const renderBooleanRow = (attribute: RatingAttribute, value: DetailValue | undefined) => {
    if (readOnly) {
      return (
        <ReadOnlyValue>
          {value?.booleanValue ? t("common:ratingDetails.yes") : t("common:ratingDetails.no")}
        </ReadOnlyValue>
      );
    }
    return (
      <>
        {[true, false].map((choice) => (
          <BooleanChoiceButton
            key={String(choice)}
            $active={value?.booleanValue === choice}
            $disabled={savingKey === attribute.key}
            aria-pressed={value?.booleanValue === choice}
            onClick={() => {
              /* Tapping the selected choice again clears the answer (the
                 backend deletes a detail sent with no value). */
              const nextValue = value?.booleanValue === choice ? null : choice;
              saveDetail(attribute.key, { scaleValue: null, booleanValue: nextValue });
              /* Scale rows track through WineRating's commit; the pills have
                 no strip, so the event fires here — after the save, so a
                 tracking failure can never block it. */
              try {
                trackEvent(trackingEvents.TRACKING_EVENT_SELECTED_PRODUCT_RATING_DETAIL, {
                  attribute_key: attribute.key,
                  boolean_value: nextValue,
                });
              } catch {
                // Analytics-only failure; the detail save has already been dispatched.
              }
            }}
          >
            {choice ? t("common:ratingDetails.yes") : t("common:ratingDetails.no")}
          </BooleanChoiceButton>
        ))}
      </>
    );
  };

  const renderRow = (attribute: RatingAttribute) => {
    const value = pendingValues[attribute.key] ?? savedValues[attribute.key];
    const hasValue = value != null && (value.scaleValue != null || value.booleanValue != null);
    if (readOnly && !hasValue) {
      return null;
    }
    return (
      <DetailRow key={attribute.key} data-testid={`rating-detail-row-${attribute.key}`}>
        <DetailLabel $form={variant === "form"}>{attribute.label}</DetailLabel>
        <DetailControl
          {...(readOnly && attribute.kind === RATING_ATTRIBUTE_KINDS.SCALE
            ? { "aria-label": `${attribute.label}: ${formatRatingScore(value?.scaleValue ?? 0)}` }
            : {})}
        >
          {attribute.kind === RATING_ATTRIBUTE_KINDS.BOOLEAN
            ? renderBooleanRow(attribute, value)
            : renderScaleRow(attribute, value)}
        </DetailControl>
      </DetailRow>
    );
  };

  if (readOnly && !hasAnySavedValue) {
    return null;
  }
  if (!readOnly && !isOpen) {
    return null;
  }

  return (
    <DetailsWrapper className={className} data-testid="product-rating-details">
      {/* Card panel only — the review form has its own headings. */}
      {!readOnly && variant !== "form" && <PanelHeading>{t("common:ratingDetails.title")}</PanelHeading>}
      <DetailRows $form={variant === "form"}>{attributes.map(renderRow)}</DetailRows>
      {!readOnly && withNote && (
        <NoteTextarea
          rows={2}
          placeholder={notePlaceholder ?? t("common:ratingDetails.notePlaceholder")}
          value={noteDraft}
          onChange={(event) => setNoteDraft(event.target.value)}
          data-testid="rating-note-input"
        />
      )}
      {!readOnly && onClose != null && (
        <DoneButton onClick={onClose}>{t("common:ratingDetails.done")}</DoneButton>
      )}
    </DetailsWrapper>
  );
};

/**
 * Self-fetching variant for the review tray: fetches the product's attribute
 * set and the member's saved details by slug. Read-only by default (rows with
 * a value render as static text, none saved → renders nothing); `editable`
 * renders the live entry rows instead, in the "form" variant so text and
 * star sizes match the rest of the review form.
 */
export const ProductRatingDetailsBySlug = ({
  slug,
  editable = false,
}: {
  slug: string;
  editable?: boolean;
}) => {
  const { data } = useQuery<GetProductRatingDetails, GetProductRatingDetailsVariables>(
    GET_PRODUCT_RATING_DETAILS,
    {
      variables: { slug },
      onError: (error) =>
        logError(error, { location: "ProductRatingDetailsBySlug", action: "GET_PRODUCT_RATING_DETAILS" }),
    }
  );

  const product = data?.productBySlug;
  if (!product?.id || !product.ratingAttributes?.length) {
    return null;
  }

  return (
    <ProductRatingDetails
      productId={product.id}
      attributes={product.ratingAttributes.flatMap((attribute) => (attribute ? [attribute] : []))}
      details={product.productRating?.details?.flatMap((detail) => (detail ? [detail] : []))}
      readOnly={!editable}
      isOpen={editable}
      withNote={false}
      variant="form"
    />
  );
};

export default ProductRatingDetails;
